// CommerceController — the framework-agnostic heart of the commercial layer.
// Orchestrates auth, entitlements, credits and the AI proxy behind ports, and
// exposes the reactive CommerceState. The browser entry (index.ts) wraps this
// as `window.JoeFlowCommerce` for the monolith to call. Fully unit-tested.

import type {
  AuthPort,
  PurchasePort,
  BackendPort,
  LocalAiPort,
  TokenProvider,
  IdProvider,
  GenerateResponse,
} from './ports';
import { BackendError } from './ports';
import type {
  AiOp,
  AiQuality,
  AiProviderTier,
  Tier,
  FeatureKey,
  CommerceState,
} from '../domain/types';
import { tierFromEntitlements } from '../domain/tiers';
import { costOfQuality } from '../domain/credits';
import { shouldWatermark as watermarkFor, canUse as featureAllowed } from '../domain/gating';

export type PaywallReason = 'out_of_credits' | 'premium_effect' | 'no_watermark' | 'manual';

export interface CommerceControllerDeps {
  auth: AuthPort;
  purchase: PurchasePort;
  backend: BackendPort;
  tokens: TokenProvider;
  ids: IdProvider;
  /** Optional local (on-device) image generator for the free 'local' tier. */
  localAi?: LocalAiPort;
  onRequireUpgrade?: (reason: PaywallReason) => void;
  /**
   * Surface a sign-in UI before the first credit-costing AI op. Resolves true
   * once a real (non-anonymous) session exists, false if the user dismissed.
   * When this dep is absent the login gate is a no-op (preserves the offline /
   * anonymous-first behaviour for unconfigured builds).
   */
  onRequireLogin?: () => Promise<boolean>;
}

// AI Act Art. 50 transparency label for locally generated artefacts (mirrors the
// server's AI_LABEL so the monolith handles local + cloud results identically).
const LOCAL_AI_LABEL = {
  aiGenerated: true,
  notice: 'Dieses Bild wurde mit KI erzeugt / This image was generated with AI',
};

const INITIAL: CommerceState = {
  tier: 'free',
  authenticated: false,
  creditBalance: 0,
  founderActive: false,
  founderRemaining: 0,
};

export class CommerceController {
  private state: CommerceState = { ...INITIAL };
  private listeners = new Set<(s: CommerceState) => void>();

  constructor(private readonly deps: CommerceControllerDeps) {}

  // ---- lifecycle ----------------------------------------------------------
  async init(): Promise<void> {
    const user = await this.deps.auth.getUser();
    const entitlements = await this.deps.purchase.getActiveEntitlements();
    const founder = await this.deps.backend.founderStatus();
    let balance = 0;
    const token = await this.deps.tokens.getAccessToken();
    if (token) balance = await this.deps.backend.getBalance(token);

    this.setState({
      tier: tierFromEntitlements(entitlements),
      authenticated: !!user && !user.isAnonymous,
      creditBalance: balance,
      founderActive: founder.active,
      founderRemaining: founder.remaining,
    });

    this.deps.auth.onAuthChange(() => void this.refreshFromAuth());
    this.deps.purchase.onEntitlementsChange((ids) =>
      this.setState({ tier: tierFromEntitlements(ids) }),
    );
  }

  // ---- reads --------------------------------------------------------------
  getState(): CommerceState {
    return { ...this.state };
  }
  getTier(): Tier {
    return this.state.tier;
  }
  shouldWatermark(): boolean {
    return watermarkFor(this.state.tier);
  }
  canUse(feature: FeatureKey): boolean {
    return featureAllowed(this.state.tier, feature);
  }
  onChange(cb: (s: CommerceState) => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  // ---- AI generation (credit-gated) --------------------------------------
  /**
   * Returns the AI result, or null when blocked (paywall / login dismissed).
   * `tier` picks the provider: 'local' (free, on-device), 'standard' (cheap
   * cloud default) or 'premium' (best cloud quality).
   */
  async generate(
    op: AiOp,
    imageDataUrl?: string,
    prompt?: string,
    tier: AiProviderTier = 'standard',
  ): Promise<GenerateResponse | null> {
    // 1. Free on-device path: no credits, no login, no cloud round trip. Falls
    //    through to the standard cloud provider if the local generator is down.
    if (op === 'transform' && tier === 'local' && this.deps.localAi && imageDataUrl) {
      const local = await this.tryLocal(imageDataUrl, prompt ?? '');
      if (local) return local;
    }

    const quality: AiQuality = tier === 'premium' ? 'premium' : 'standard';
    const cost = costOfQuality(op, quality);

    // 2. Login gate: a real (non-anonymous) account is required before the first
    //    credit-costing op. No-op when the dep isn't wired (offline build).
    if (cost > 0 && this.deps.onRequireLogin) {
      const current = await this.deps.auth.getUser();
      if (!current || current.isAnonymous) {
        const signedIn = await this.deps.onRequireLogin();
        if (!signedIn) return null;
      }
    }

    // 3. Optimistic local guard: avoid a round trip when we know there are no credits.
    if (cost > 0 && this.state.creditBalance < cost) {
      this.requireUpgrade('out_of_credits');
      return null;
    }

    // Ensure a session so the server can attribute credits.
    const user = await this.deps.auth.ensureSession();
    await this.deps.purchase.identify(user.id);

    const token = await this.deps.tokens.getAccessToken();
    const req = {
      op,
      ...(imageDataUrl !== undefined ? { imageDataUrl } : {}),
      ...(prompt !== undefined ? { prompt } : {}),
      requestId: this.deps.ids.newId(),
      quality,
    };

    try {
      const res = await this.deps.backend.generate(req, token ?? '');
      this.setState({ creditBalance: res.balance });
      return res;
    } catch (err) {
      if (err instanceof BackendError && err.code === 'insufficient_credits') {
        this.requireUpgrade('out_of_credits');
        return null;
      }
      throw err;
    }
  }

  /** Attempt on-device generation; null if unavailable so the caller falls back. */
  private async tryLocal(imageDataUrl: string, prompt: string): Promise<GenerateResponse | null> {
    const local = this.deps.localAi;
    if (!local) return null;
    try {
      if (!(await local.isAvailable())) return null;
      const out = await local.generate(imageDataUrl, prompt);
      if (!out.imageUrl) return null;
      // Wrap in the fal-like result shape so normalizeAiResult handles it like cloud.
      return {
        result: { images: [{ url: out.imageUrl }] },
        balance: this.state.creditBalance,
        label: LOCAL_AI_LABEL,
      };
    } catch {
      return null; // local generator errored → fall back to cloud
    }
  }

  requireUpgrade(reason: PaywallReason): void {
    this.deps.onRequireUpgrade?.(reason);
  }

  // ---- purchases ----------------------------------------------------------
  async purchase(tier: Tier, period: 'monthly' | 'annual'): Promise<void> {
    const entitlements = await this.deps.purchase.purchase(tier, period);
    this.setState({ tier: tierFromEntitlements(entitlements) });
    await this.refreshBalance();
  }

  async restore(): Promise<void> {
    const entitlements = await this.deps.purchase.restore();
    this.setState({ tier: tierFromEntitlements(entitlements) });
  }

  /**
   * Mock "Stripe" credit top-up (TEST ONLY). The server grants a fixed credit
   * pack to the (non-anonymous) user; we sync the authoritative balance back.
   * No real payment is taken. Throws on failure (e.g. anonymous → login_required)
   * so the paywall button can show a retry state.
   */
  async topUp(): Promise<number> {
    const user = await this.deps.auth.ensureSession();
    await this.deps.purchase.identify(user.id);
    const token = await this.deps.tokens.getAccessToken();
    if (!token) throw new BackendError('topup_no_token', 401);
    const balance = await this.deps.backend.topUp(token, this.deps.ids.newId());
    this.setState({ creditBalance: balance });
    return balance;
  }

  async refreshFounder(): Promise<void> {
    const f = await this.deps.backend.founderStatus();
    this.setState({ founderActive: f.active, founderRemaining: f.remaining });
  }

  // ---- internals ----------------------------------------------------------
  private async refreshBalance(): Promise<void> {
    const token = await this.deps.tokens.getAccessToken();
    if (token) this.setState({ creditBalance: await this.deps.backend.getBalance(token) });
  }

  private async refreshFromAuth(): Promise<void> {
    const user = await this.deps.auth.getUser();
    const entitlements = await this.deps.purchase.getActiveEntitlements();
    this.setState({
      authenticated: !!user && !user.isAnonymous,
      tier: tierFromEntitlements(entitlements),
    });
    await this.refreshBalance();
  }

  private setState(patch: Partial<CommerceState>): void {
    this.state = { ...this.state, ...patch };
    const snapshot = this.getState();
    for (const cb of this.listeners) cb(snapshot);
  }
}
